// Phase 34 — first-run onboarding. A welcome modal + 3-step guided checklist
// shown once per browser (localStorage flag). No new API calls — it links into
// existing pages (Settings, Agent Download, Users). Mounted globally; renders
// nothing unless the user is authenticated and hasn't onboarded yet.

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Download, UserPlus, Rocket, Check, ArrowRight } from 'lucide-react';

const FLAG = 'dk_onboarded_v1';

const STEPS = [
  { icon: Building2, title: 'Set up your company', desc: 'Add your company name, logo, and timezone so reports and emails look polished.', cta: 'Open settings', to: '/settings' },
  { icon: Download, title: 'Install the agent', desc: 'Download the Windows agent and enroll your first device — data flows in within minutes.', cta: 'Download agent', to: '/agent-download' },
  { icon: UserPlus, title: 'Invite your team', desc: 'Add managers and team members so everyone sees the right slice of productivity data.', cta: 'Invite team', to: '/users' },
];

export function OnboardingWizard() {
  const { user } = useAuth();
  const navigate = useSafeNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0 = welcome screen, 1..3 = steps

  useEffect(() => {
    if (user && !localStorage.getItem(FLAG)) {
      const t = setTimeout(() => setOpen(true), 800); // let the dashboard paint first
      return () => clearTimeout(t);
    }
  }, [user]);

  const finish = () => { try { localStorage.setItem(FLAG, '1'); } catch { /* ignore */ } setOpen(false); };
  const go = (to: string) => { finish(); navigate(to); };

  if (!user) return null;
  const s = step >= 1 ? STEPS[step - 1] : null;
  const StepIcon = s?.icon; // capitalized so it's usable as a JSX element

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* gradient header */}
        <div className="relative bg-[#0b0f1a] px-7 pt-7 pb-6 text-white overflow-hidden">
          <div className="absolute inset-0 auth-grid opacity-50" />
          <div className="absolute -top-16 -right-10 h-44 w-44 rounded-full bg-blue-600/30 blur-3xl auth-blob" />
          <div className="relative z-10">
            {step === 0 ? (
              <>
                <div className="h-11 w-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center mb-3"><Rocket className="h-5 w-5 text-blue-400" /></div>
                <h2 className="text-2xl font-extrabold">Welcome to Prodify360 🎉</h2>
                <p className="mt-1.5 text-sm text-slate-400">Let’s get your workspace up and running in three quick steps.</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  {STEPS.map((_, i) => (
                    <span key={i} className={'h-1.5 rounded-full transition-all ' + (i === step - 1 ? 'w-7 bg-blue-400' : (step - 1 > i ? 'w-4 bg-emerald-400' : 'w-4 bg-white/15'))} />
                  ))}
                </div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Step {step} of {STEPS.length}</p>
                <h2 className="text-xl font-extrabold mt-1">{s!.title}</h2>
              </>
            )}
          </div>
        </div>

        {/* body */}
        <div className="px-7 py-6">
          {step === 0 ? (
            <ul className="space-y-3">
              {STEPS.map((st) => (
                <li key={st.title} className="flex items-start gap-3">
                  <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><st.icon className="h-4 w-4" /></span>
                  <div><p className="text-sm font-semibold">{st.title}</p><p className="text-xs text-muted-foreground">{st.desc}</p></div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center text-center py-2">
              <span className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/20 text-primary flex items-center justify-center mb-3">{StepIcon && <StepIcon className="h-6 w-6" />}</span>
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">{s!.desc}</p>
              <Button className="mt-5 w-full" onClick={() => go(s!.to)}>{s!.cta}<ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <button onClick={finish} className="text-xs text-muted-foreground hover:text-foreground">Skip for now</button>
            {step === 0 ? (
              <Button size="sm" onClick={() => setStep(1)}>Get started<ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : step < STEPS.length ? (
              <Button size="sm" variant="outline" onClick={() => setStep(step + 1)}>Next step<ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button size="sm" onClick={finish}><Check className="h-4 w-4 mr-1" />Finish</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
